/* ============================================================================
   ivSolver.ts
   Back-solves a Pokemon's individual values (Atk / Def / HP IV) from the
   CP and HP shown on the encounter screen, given the encounter context
   (which fixes the level and IV floor).

   Encounter rules baked in (May 2026 game state):
     - Wild               level 1-35 by trainer level, weather +5
     - Raid (T1-T6, Mega) fixed level 20, weather 25, IV floor 10/10/10
     - Research (Field/Timed/Special) fixed level 15, weather 20, floor 10
     - Rocket Shadow      fixed level 8, IV floor 6/6/6
     - Purified           fixed level 25, IV floor 4/4/4 (post-purify CP/HP)

   Math:
     CP = max(10, floor((Atk * sqrt(Def) * sqrt(HP) * CPM^2) / 10))
     HP = max(10, floor((baseStamina + staIV) * CPM))
     where Atk = baseAtk + atkIV, etc.

   Strategy: HP is a 1D check on staIV given level, so we bucket candidates
   by HP first, then iterate atkIV/defIV pairs to match CP. This stays fast
   even for the all-IV case (max 4096 combos).

   No external deps. Pure functions.
   ============================================================================ */

// Level-multiplier table (CPM). Whole levels are the values Niantic ships
// in the Game Master; half-levels are what the game derives from them,
// sqrt((cpm(L)^2 + cpm(L+1)^2) / 2). Values mirror PvPoke's table, which
// agrees with Poké Genie rank for rank (2026-09-07 Eevee 9/9/5 check).
// The previous table had ad-hoc half-level entries at 20.5-29.5 and
// 41.5-49.5 and a wrong 50.5 / 51, which shifted CP at the league caps and
// mis-ranked spreads by a place or two.
const CPM_TABLE: Record<number, number> = {
  1: 0.093999997,
  1.5: 0.135137431,
  2: 0.16639787,
  2.5: 0.192650914,
  3: 0.21573247,
  3.5: 0.236572655,
  4: 0.255720049,
  4.5: 0.273530381,
  5: 0.290249884,
  5.5: 0.306057381,
  6: 0.321087599,
  6.5: 0.335445032,
  7: 0.349212676,
  7.5: 0.362457749,
  8: 0.375235587,
  8.5: 0.387592411,
  9: 0.399567276,
  9.5: 0.41119355,
  10: 0.422500014,
  10.5: 0.432926413,
  11: 0.443107545,
  11.5: 0.453059954,
  12: 0.462798387,
  12.5: 0.472336078,
  13: 0.481684953,
  13.5: 0.49085581,
  14: 0.499858439,
  14.5: 0.508701757,
  15: 0.517393947,
  15.5: 0.525942509,
  16: 0.534354329,
  16.5: 0.542635762,
  17: 0.550792694,
  17.5: 0.558830599,
  18: 0.56675452,
  18.5: 0.574569148,
  19: 0.582278907,
  19.5: 0.589887912,
  20: 0.59740001,
  20.5: 0.604823658,
  21: 0.612157285,
  21.5: 0.619404111,
  22: 0.626567125,
  22.5: 0.633649182,
  23: 0.640652955,
  23.5: 0.647580963,
  24: 0.654435635,
  24.5: 0.661219264,
  25: 0.667934,
  25.5: 0.674581899,
  26: 0.68116492,
  26.5: 0.687684906,
  27: 0.694143653,
  27.5: 0.700542893,
  28: 0.706884205,
  28.5: 0.713169102,
  29: 0.719399095,
  29.5: 0.725575617,
  30: 0.731700003,
  30.5: 0.734741011,
  31: 0.737769485,
  31.5: 0.740785575,
  32: 0.743789434,
  32.5: 0.746781209,
  33: 0.749761045,
  33.5: 0.752729105,
  34: 0.755685508,
  34.5: 0.758630367,
  35: 0.761563838,
  35.5: 0.764486065,
  36: 0.767397165,
  36.5: 0.770297274,
  37: 0.773186505,
  37.5: 0.776064946,
  38: 0.77893275,
  38.5: 0.781790065,
  39: 0.784636974,
  39.5: 0.787473584,
  40: 0.790300012,
  40.5: 0.792803951,
  41: 0.795300007,
  41.5: 0.797803921,
  42: 0.800300002,
  42.5: 0.802803892,
  43: 0.805299997,
  43.5: 0.807803863,
  44: 0.810299993,
  44.5: 0.812803835,
  45: 0.815299988,
  45.5: 0.817803807,
  46: 0.820299983,
  46.5: 0.822803779,
  47: 0.825299978,
  47.5: 0.827803751,
  48: 0.830299973,
  48.5: 0.832803753,
  49: 0.835300028,
  49.5: 0.837803756,
  50: 0.840300024,
  50.5: 0.842803729,
  51: 0.845300019,
};

export function cpmAtLevel(level: number): number {
  const cpm = CPM_TABLE[level];
  if (cpm == null) {
    throw new Error(`No CPM for level ${level}`);
  }
  return cpm;
}

// ── Encounter context ─────────────────────────────────────────────────────

export type EncounterType =
  | "wild"
  | "raid"
  | "research"
  | "rocket_shadow"
  | "purified"
  | "hatch"
  | "trade";

export interface EncounterContext {
  type: EncounterType;
  // Whether the catch is weather-boosted. Wild +5, Raid +5, Research +5.
  weatherBoosted: boolean;
  // For wild catches only, the trainer's level (caps wild encounter level
  // at min(trainerLevel, 30) + 5 with boost).
  trainerLevel?: number;
}

// Returns the candidate level(s) the encounter could be at, plus the IV
// floor for each component. Wild returns a wide level set; the rest are
// single-level.
export interface EncounterProfile {
  levels: number[];   // possible Pokemon levels in this encounter
  ivFloor: number;    // minimum IV value for atk, def, sta independently
}

export function profileForEncounter(ctx: EncounterContext): EncounterProfile {
  switch (ctx.type) {
    case "raid": {
      return {
        levels: [ctx.weatherBoosted ? 25 : 20],
        ivFloor: 10,
      };
    }
    case "research": {
      return {
        levels: [ctx.weatherBoosted ? 20 : 15],
        ivFloor: 10,
      };
    }
    case "hatch": {
      return {
        levels: [20],
        ivFloor: 10,
      };
    }
    case "rocket_shadow": {
      return {
        levels: [8],
        ivFloor: 6,
      };
    }
    case "purified": {
      return {
        levels: [25],
        ivFloor: 4,
      };
    }
    case "trade": {
      // Trade preserves the Pokemon's level; we can't infer it from CP
      // alone without more context. Return a wide range so the solver
      // still produces a list, but UI should warn this is approximate.
      const levels: number[] = [];
      for (let l = 1; l <= 50; l += 0.5) levels.push(l);
      return { levels, ivFloor: 0 };
    }
    case "wild":
    default: {
      // Wild encounter level = uniform(1, min(trainerLevel, 30)) + (boost ? 5 : 0)
      // We don't know the rolled level, so emit the full possible range.
      const trainerLevel = ctx.trainerLevel ?? 40;
      const cap = Math.min(trainerLevel, 30);
      const boost = ctx.weatherBoosted ? 5 : 0;
      const minL = 1 + boost;
      const maxL = cap + boost;
      const levels: number[] = [];
      for (let l = minL; l <= maxL; l += 1) levels.push(l);
      return { levels, ivFloor: 0 };
    }
  }
}

// ── Core math ─────────────────────────────────────────────────────────────

export interface BaseStats {
  atk: number;
  def: number;
  sta: number;
}

export function computeCp(
  base: BaseStats,
  iv: { atk: number; def: number; sta: number },
  level: number,
): number {
  const cpm = cpmAtLevel(level);
  const a = base.atk + iv.atk;
  const d = base.def + iv.def;
  const s = base.sta + iv.sta;
  const raw = (a * Math.sqrt(d) * Math.sqrt(s) * cpm * cpm) / 10;
  return Math.max(10, Math.floor(raw));
}

export function computeHp(
  base: BaseStats,
  staIv: number,
  level: number,
): number {
  const cpm = cpmAtLevel(level);
  return Math.max(10, Math.floor((base.sta + staIv) * cpm));
}

// ── Solver ────────────────────────────────────────────────────────────────

export interface IvCombo {
  atk: number;
  def: number;
  sta: number;
  level: number;
  cp: number;     // computed CP for this combo at this level
  hp: number;     // computed HP for this combo at this level
  percent: number; // (atk + def + sta) / 45
}

export interface IvRange {
  atkMin: number; atkMax: number;
  defMin: number; defMax: number;
  staMin: number; staMax: number;
  // Possible levels (deduped, sorted).
  levels: number[];
  // Total candidate combos that match.
  combos: IvCombo[];
  // 0..1, percentile of the BEST possible combo against all 4096.
  // 1 = guaranteed hundo, 0 = guaranteed nothing.
  bestPercent: number;
  worstPercent: number;
  // True if at least one matching combo is 15/15/15.
  hundoPossible: boolean;
}

export interface SolverInput {
  base: BaseStats;
  cp: number;
  hp: number;
  encounter: EncounterContext;
}

export function solveIvs(input: SolverInput): IvRange {
  const profile = profileForEncounter(input.encounter);
  const combos: IvCombo[] = [];

  for (const level of profile.levels) {
    // Step 1: which staIVs match the HP reading at this level?
    const staMatches: number[] = [];
    for (let staIv = profile.ivFloor; staIv <= 15; staIv++) {
      if (computeHp(input.base, staIv, level) === input.hp) {
        staMatches.push(staIv);
      }
    }
    if (staMatches.length === 0) continue;

    // Step 2: for each staIV, find atk/def pairs that match CP.
    for (const staIv of staMatches) {
      for (let atkIv = profile.ivFloor; atkIv <= 15; atkIv++) {
        for (let defIv = profile.ivFloor; defIv <= 15; defIv++) {
          const cp = computeCp(input.base, { atk: atkIv, def: defIv, sta: staIv }, level);
          if (cp === input.cp) {
            combos.push({
              atk: atkIv,
              def: defIv,
              sta: staIv,
              level,
              cp,
              hp: input.hp,
              percent: (atkIv + defIv + staIv) / 45,
            });
          }
        }
      }
    }
  }

  if (combos.length === 0) {
    return {
      atkMin: 0, atkMax: 0,
      defMin: 0, defMax: 0,
      staMin: 0, staMax: 0,
      levels: [],
      combos: [],
      bestPercent: 0,
      worstPercent: 0,
      hundoPossible: false,
    };
  }

  let atkMin = 15, atkMax = 0;
  let defMin = 15, defMax = 0;
  let staMin = 15, staMax = 0;
  let bestPercent = 0;
  let worstPercent = 1;
  let hundoPossible = false;
  const levelSet = new Set<number>();

  for (const c of combos) {
    if (c.atk < atkMin) atkMin = c.atk;
    if (c.atk > atkMax) atkMax = c.atk;
    if (c.def < defMin) defMin = c.def;
    if (c.def > defMax) defMax = c.def;
    if (c.sta < staMin) staMin = c.sta;
    if (c.sta > staMax) staMax = c.sta;
    if (c.percent > bestPercent) bestPercent = c.percent;
    if (c.percent < worstPercent) worstPercent = c.percent;
    if (c.atk === 15 && c.def === 15 && c.sta === 15) hundoPossible = true;
    levelSet.add(c.level);
  }

  return {
    atkMin, atkMax,
    defMin, defMax,
    staMin, staMax,
    levels: Array.from(levelSet).sort((a, b) => a - b),
    combos,
    bestPercent,
    worstPercent,
    hundoPossible,
  };
}

// ── Helpers for UI ────────────────────────────────────────────────────────

// Human-readable label for an encounter type.
/**
 * CP-ONLY solve (2026-09-22, the wild-encounter HUD): the encounter screen
 * prints the CP but not the HP, so every IV triple at every level the
 * encounter allows is tried against the CP alone. 35 levels x 4096 spreads
 * is ~143k CP evaluations, a few milliseconds. A wild encounter's weather
 * boost is not readable from the banner, so `wild` unions the plain and
 * boosted level windows (1-30 and 6-35, floor 0).
 */
export interface CpOnlyRange {
  combos: number;
  bestPercent: number;
  worstPercent: number;
  levelMin: number;
  levelMax: number;
  hundoPossible: boolean;
}

export function solveIvsFromCp(input: { base: BaseStats; cp: number; encounter: EncounterContext }): CpOnlyRange | null {
  const profiles = [profileForEncounter(input.encounter)];
  if (input.encounter.type === "wild") {
    profiles.push(profileForEncounter({ ...input.encounter, weatherBoosted: !input.encounter.weatherBoosted }));
  }
  const levels = new Set<number>();
  let floor = Infinity;
  for (const p of profiles) {
    for (const l of p.levels) levels.add(l);
    floor = Math.min(floor, p.ivFloor);
  }
  if (!Number.isFinite(floor)) floor = 0;
  let combos = 0;
  let best = -1;
  let worst = 2;
  let levelMin = Infinity;
  let levelMax = -Infinity;
  let hundo = false;
  for (const level of levels) {
    const cpm = cpmAtLevel(level);
    const k = (cpm * cpm) / 10;
    for (let d = floor; d <= 15; d++) {
      const sd = Math.sqrt(input.base.def + d);
      for (let s = floor; s <= 15; s++) {
        const sds = sd * Math.sqrt(input.base.sta + s);
        for (let a = floor; a <= 15; a++) {
          const cp = Math.max(10, Math.floor((input.base.atk + a) * sds * k));
          if (cp !== input.cp) continue;
          combos++;
          const pct = (a + d + s) / 45;
          if (pct > best) best = pct;
          if (pct < worst) worst = pct;
          if (level < levelMin) levelMin = level;
          if (level > levelMax) levelMax = level;
          if (a === 15 && d === 15 && s === 15) hundo = true;
        }
      }
    }
  }
  if (combos === 0) return null;
  return { combos, bestPercent: best, worstPercent: worst, levelMin, levelMax, hundoPossible: hundo };
}

export function encounterLabel(type: EncounterType): string {
  switch (type) {
    case "wild":          return "Wild";
    case "raid":          return "Raid";
    case "research":      return "Research";
    case "rocket_shadow": return "Rocket Shadow";
    case "purified":      return "Purified";
    case "hatch":         return "Hatched";
    case "trade":         return "Trade";
  }
}

// Bucket a percentile into a friendly label.
export function percentileLabel(pct: number): string {
  if (pct >= 1) return "Hundo (100%)";
  if (pct >= 0.9778) return "Top 1% (98%+)";
  if (pct >= 0.93) return "Top 5% (93%+)";
  if (pct >= 0.82) return "Top 25% (82%+)";
  if (pct >= 0.66) return "Above average";
  return "Average or below";
}

// Friendly summary string for the IvRange.
export function rangeSummary(range: IvRange): string {
  if (range.combos.length === 0) {
    return "No matching IV combo. Check your CP and HP, or your encounter type.";
  }
  if (range.combos.length === 1) {
    const c = range.combos[0];
    return `${c.atk}/${c.def}/${c.sta} (${Math.round(c.percent * 100)}%)`;
  }
  const a = range.atkMin === range.atkMax ? `${range.atkMin}` : `${range.atkMin}-${range.atkMax}`;
  const d = range.defMin === range.defMax ? `${range.defMin}` : `${range.defMin}-${range.defMax}`;
  const s = range.staMin === range.staMax ? `${range.staMin}` : `${range.staMin}-${range.staMax}`;
  return `${a}/${d}/${s} (${range.combos.length} combos)`;
}
